import { createListenerMiddleware } from '@reduxjs/toolkit';

import { EVENT_FILTER_VALUE_CHANGED } from '@/constants/events';
import formattedFiltersChoiceSelector from '@/features/choice/selectors/formattedFilters';
import * as filtersSelectors from '@/features/filters/selectors';
import type { RootStateType } from '@/features/store';
import { IChoiceFilters, IChoiceHistoryItem } from '@/interfaces/choice.interfaces';
import { FilterBlocksEnum } from '@/interfaces/constructor.interfaces';
import { CustomHistoryActionEnum } from '@/interfaces/history.interfaces';
import InjectContainer from '@/services/InjectContainer';
import { INavigationService } from '@/services/NavigationService/types';
import ServiceTypes from '@/services/ServiceTypes';
import deepCopy from '@/utils/deepCopy';
import isDefined from '@/utils/isDefined';
import sendEvent from '@/utils/sendEvent';

import { choiceSlice } from '../reducer';

const listenerMiddleware = createListenerMiddleware();

/**
 * Слушатель, срабатывающий на установку фильтров, при котором
 * происходит сброс зависимых фильтров
 */
listenerMiddleware.startListening({
  actionCreator: choiceSlice.actions.setFilters,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootStateType;
    const filtersState = state.choice.filters;

    const changedFilters = action.payload;

    for (const [filterKey, filterValue] of Object.entries(changedFilters)) {
      const filterItem = filtersSelectors.itemByKeySelector(state, {
        filterKey,
      });

      if (!filterItem || isDefined(filterValue)) {
        continue;
      }

      if (!filterItem.dependents || !filterItem.dependents.length) {
        continue;
      }

      const resolvedFilters = filterItem.dependents.reduce<IChoiceFilters>(
        (acc, dependentFilterKey) => {
          const dependentFilterValue = filtersState?.[dependentFilterKey];

          if (!isDefined(dependentFilterValue)) {
            return acc;
          }

          return {
            ...acc,
            [`${dependentFilterKey}`]: undefined,
          };
        },
        {},
      );

      if (Object.keys(resolvedFilters).length) {
        listenerApi.dispatch(choiceSlice.actions.setFilters(resolvedFilters));
      }
    }
  },
});

/**
 * Слушатель, срабатывающий на установку фильтров, при котором
 * происходит установка query-параметров в url
 */
listenerMiddleware.startListening({
  actionCreator: choiceSlice.actions.setFilters,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootStateType;
    const filtersChoiceState = formattedFiltersChoiceSelector(state);

    const navigationService = InjectContainer.get<INavigationService>(
      ServiceTypes.Navigation,
    );

    navigationService.replace(
      {
        search: `?${new URLSearchParams(filtersChoiceState).toString()}`.replaceAll(
          '%2C',
          '|',
        ),
      },
      {
        action: CustomHistoryActionEnum.SetFilters,
      },
    );
  },
});

/**
 * Слушатель, срабатывающий на сброс фильтров, при котором
 * происходит сброс query-параметров в url
 */
listenerMiddleware.startListening({
  actionCreator: choiceSlice.actions.resetFilters,
  effect: () => {
    const navigationService = InjectContainer.get<INavigationService>(
      ServiceTypes.Navigation,
    );

    navigationService.replace(
      {
        search: '',
      },
      {
        action: CustomHistoryActionEnum.ResetFilters,
      },
    );
  },
});

/**
 * Слушатель, срабатывающий на установку фильтров, при котором
 * происходит заполнение истории по выбранным значениям
 */
listenerMiddleware.startListening({
  actionCreator: choiceSlice.actions.setFilters,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootStateType;
    const previousState = listenerApi.getOriginalState() as RootStateType;

    const history = deepCopy(state.choice.history);
    const previousFilters = previousState.choice.filters;

    const changedFilters = action.payload;

    const historyItemsToAdd: Array<IChoiceHistoryItem> = [];
    const historyItemsToRemove: Array<IChoiceHistoryItem> = [];
    const historyItemsToChange: Array<IChoiceHistoryItem> = [];

    for (const [filterKey, filterValue] of Object.entries(changedFilters)) {
      const filterItem = filtersSelectors.itemByKeySelector(state, {
        filterKey,
      });
      const previousFilterValue = previousFilters?.[filterKey];

      if (!filterItem) {
        continue;
      }

      sendEvent(EVENT_FILTER_VALUE_CHANGED, {
        filterKey,
        value: filterValue,
        previousValue: previousFilterValue,
      });

      // Получить историю по фильтру
      const filterHistoryItems = history.filter(
        historyItem => historyItem.key === filterKey,
      );
      const filterHistoryItemValues = filterHistoryItems.map(({ value }) => value);

      const isFilterRemoval =
        typeof filterValue === 'undefined' ||
        (Array.isArray(filterValue) && !filterValue.length);

      if (isFilterRemoval) {
        historyItemsToRemove.push(...filterHistoryItems);
      } else {
        switch (filterItem.type) {
          case FilterBlocksEnum.FilterLocation:
          case FilterBlocksEnum.FilterSelect:
          case FilterBlocksEnum.FilterSlider:
          case FilterBlocksEnum.FilterMultipleSelect:
          case FilterBlocksEnum.FilterCheckboxGroup:
          case FilterBlocksEnum.FilterModelCheckboxGroup:
          case FilterBlocksEnum.FilterColorPicker:
          case FilterBlocksEnum.FilterDuoColorPicker: {
            const resolvedFilterValue = Array.isArray(filterValue)
              ? filterValue
              : [filterValue];
            historyItemsToRemove.push(
              ...filterHistoryItems.filter(
                historyItem => !resolvedFilterValue.includes(historyItem.value),
              ),
            );
            historyItemsToAdd.push(
              ...resolvedFilterValue
                .filter(value => !filterHistoryItemValues.includes(value))
                .map(value => ({
                  key: filterKey,
                  value,
                })),
            );
            break;
          }
          case FilterBlocksEnum.FilterRangeSlider: {
            if (!filterHistoryItems.length) {
              historyItemsToAdd.push({
                key: filterKey,
                value: filterValue,
              });
            } else {
              historyItemsToChange.push({
                ...filterHistoryItems[0],
                value: filterValue,
              });
            }
            break;
          }
          default: {
            break;
          }
        }
      }
    }

    const resolvedHistory = history
      .reduce<Array<IChoiceHistoryItem>>((acc, historyItem) => {
        const historyItemToRemoveIndex = historyItemsToRemove.findIndex(
          i => i === historyItem,
        );
        if (historyItemToRemoveIndex !== -1) {
          return acc;
        }
        const historyItemToChangeIndex = historyItemsToChange.findIndex(
          i => i.key === historyItem.key,
        );
        if (historyItemToChangeIndex !== -1) {
          return [...acc, historyItemsToChange[historyItemToChangeIndex]];
        }
        return [...acc, historyItem];
      }, [])
      .concat(...historyItemsToAdd);

    listenerApi.dispatch(choiceSlice.actions.setHistory(resolvedHistory));
  },
});

/**
 * Слушатель, срабатывающий на сброс фильтров, при котором
 * происходит сброс истории
 */
listenerMiddleware.startListening({
  actionCreator: choiceSlice.actions.resetFilters,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(choiceSlice.actions.resetHistory());
  },
});

export default listenerMiddleware.middleware;
